/**
 * Mirrors apps/agent-portal-api/src/modules/user/roles.ts.
 * Kept in sync manually.
 *
 * Unified role model: every user has exactly one ACCOUNT role (who they are —
 * replaces the legacy user_type in the UI) plus zero or more PERMISSION roles
 * (what they can do). `admin` is the wildcard.
 */

import type { UserType } from "./api";

export const ACCOUNT_ROLES = [
  "internal",
  "agent",
  "sub_agent",
  "godaddy_merchant",
] as const;

export const PERMISSION_ROLES = [
  "admin",
  "manager",
  "finance",
  "support",
  "viewer",
] as const;

export const ROLES = [...ACCOUNT_ROLES, ...PERMISSION_ROLES] as const;

export type AccountRole = (typeof ACCOUNT_ROLES)[number];
export type PermissionRole = (typeof PERMISSION_ROLES)[number];
export type AppRole = (typeof ROLES)[number];

export const ROLE_DESCRIPTIONS: Record<
  AppRole,
  { label: string; description: string; kind: "account" | "permission" }
> = {
  internal: {
    label: "Internal",
    description: "MSD staff member — full portal scope.",
    kind: "account",
  },
  agent: {
    label: "Agent",
    description: "Sales agent — their own merchants and applications.",
    kind: "account",
  },
  sub_agent: {
    label: "Sub-agent",
    description: "Works under a parent agent.",
    kind: "account",
  },
  godaddy_merchant: {
    label: "GoDaddy merchant",
    description: "Merchant — restricted to Poynt Collect + Helpdesk.",
    kind: "account",
  },
  admin: {
    label: "Admin",
    description: "Manage users, approve access, all modules.",
    kind: "permission",
  },
  manager: {
    label: "Manager",
    description: "Applications + merchant approval.",
    kind: "permission",
  },
  finance: {
    label: "Finance",
    description: "Residuals, revenue, commissions.",
    kind: "permission",
  },
  support: {
    label: "Support",
    description: "Helpdesk, tickets, AI call log.",
    kind: "permission",
  },
  viewer: {
    label: "Viewer",
    description: "Read-only across visible modules.",
    kind: "permission",
  },
};

const USER_TYPE_TO_ACCOUNT_ROLE: Record<UserType, AccountRole> = {
  INTERNAL: "internal",
  AGENT: "agent",
  SUB_AGENT: "sub_agent",
  GODADDY_MERCHANT: "godaddy_merchant",
};

const ACCOUNT_ROLE_TO_USER_TYPE: Record<AccountRole, UserType> = {
  internal: "INTERNAL",
  agent: "AGENT",
  sub_agent: "SUB_AGENT",
  godaddy_merchant: "GODADDY_MERCHANT",
};

export function isAccountRole(value: string): value is AccountRole {
  return (ACCOUNT_ROLES as readonly string[]).includes(value);
}

export function userTypeToAccountRole(userType: UserType): AccountRole {
  return USER_TYPE_TO_ACCOUNT_ROLE[userType];
}

export function accountRoleToUserType(role: AccountRole): UserType {
  return ACCOUNT_ROLE_TO_USER_TYPE[role];
}

/** The single account role within a flat roles list, if present. */
export function getAccountRole(
  roles: string[] | undefined | null,
): AccountRole | undefined {
  return roles?.find((r): r is AccountRole => isAccountRole(r));
}

/** Permission (non-account) roles within a flat roles list. */
export function permissionRoles(roles: string[] | undefined | null): string[] {
  return (roles ?? []).filter((r) => !isAccountRole(r));
}

export function hasRole(
  userRoles: string[] | undefined | null,
  required: AppRole | AppRole[],
): boolean {
  if (!userRoles) return false;
  if (userRoles.includes("admin")) return true;
  const allowed = Array.isArray(required) ? required : [required];
  return allowed.some((r) => userRoles.includes(r));
}
